import React, { useContext, useEffect, useState } from 'react'
import { ProjectContext } from '../../context/ProjectContext'
import { Transcript } from './transcript'
import { EmptyState } from './transcripts/EmptyState'
import cnt from '../../constants'
import { NeedTooltip } from './transcripts/tooltips/needTooltip'
import { QuoteTooltip } from './transcripts/tooltips/quoteTooltip'
import { QATooltip } from './transcripts/tooltips/qaTooltip'
import { FeedbackTooltip } from './transcripts/tooltips/feedbackTooltip'

export function Transcripts () {
  const { transcripts, meetings, participants, project, notes, feedbacks, qas, quotes } = useContext(ProjectContext)
  const [texts, setTexts] = useState([])

  useEffect(() => {
    const newMeetings = []
    for (const meet of meetings) {
      const transcriptsByMeeting = transcripts
        .filter((t) => t.meeting_id === meet.id)
        .sort((a, b) => {
          const numberPattern = /\[(\d+(\.\d+)?)\]/
          const numberA = a.transcript.match(numberPattern) ? parseFloat(a.transcript.match(numberPattern)[1]) : 0
          const numberB = b.transcript.match(numberPattern) ? parseFloat(b.transcript.match(numberPattern)[1]) : 0
          return numberA - numberB
        })

      let lastSpeaker = ''
      const groupBySpeaker = []
      transcriptsByMeeting.forEach((doc) => {
        if (doc.participant_id !== '' && lastSpeaker === doc.participant_id) {
          groupBySpeaker[groupBySpeaker.length - 1].transcript += '\n' + doc.transcript.trim()
        } else {
          lastSpeaker = doc.participant_id
          const time = doc.transcript.match(/\[[^\]]+\]/)[0]
          const timeSeconds = parseFloat(time.substring(1, time.length - 1))
          const hours = Math.floor(timeSeconds / 60 / 60)
          const minutes = Math.floor(timeSeconds / 60)
          const secondsLeft = Math.floor(timeSeconds % 60)
          const timeFormatted = `${hours}:${(minutes < 10 && '0') + minutes}:${(secondsLeft < 10 && '0') + secondsLeft}`
          groupBySpeaker.push({
            participant_id: doc.participant_id,
            time: timeFormatted,
            transcript: doc.transcript.trim(),
            id: doc.id,
          })
        }
      })

      const splittedTranscripts = []
      groupBySpeaker.forEach((doc) => {
        const regex = /\[(\d+\.\d+)\]\s*(.+)/g
        let match
        const sentences = []
        while ((match = regex.exec(doc.transcript.trim())) !== null) {
          const start = parseFloat(match[1])
          const text = match[2]
          sentences.push({ text, start })
        }
        splittedTranscripts.push({ participant_id: doc.participant_id, time: doc.time, id: doc.id, sentences })
      })

      const highlights = []
      for (const need of notes.filter(n => n.meetingId === meet.id)) {
        highlights.push({
          note_type: 'need',
          start_time: need.start_time,
          end_time: need.end_time,
          color: cnt.notesColors.needs,
          tooltip: (
            <NeedTooltip
              id={need.id}
              content={need.content}
              startTime={need.start_time}
              participantId={need.participant_id}
              whoSaidIt={need.who_said_it}
              referent={need.referent}
            />
          ),
        })
      }
      for (const feedback of feedbacks.filter(f => f.meetingId === meet.id)) {
        highlights.push({
          note_type: 'feedback',
          start_time: feedback.start_time,
          end_time: feedback.end_time,
          color:
            feedback.sentiment_analysis === 'positive'
              ? cnt.notesColors.feedback.positive
              : feedback.sentiment_analysis === 'negative'
                ? cnt.notesColors.feedback.negative
                : cnt.notesColors.feedback.neutral,
          tooltip: (
            <FeedbackTooltip
              id={feedback.id}
              content={feedback.content}
              startTime={feedback.start_time}
              participantId={feedback.participant_id}
              whoSaidIt={feedback.who_said_it}
              sentimentAnalysis={feedback.sentiment_analysis}
            />
          ),
        })
      }
      for (const quote of quotes.filter(q => q.meetingId === meet.id)) {
        highlights.push({
          note_type: 'quote',
          start_time: quote.start_time,
          end_time: quote.end_time,
          color: cnt.notesColors.quotes,
          tooltip: (
            <QuoteTooltip
              id={quote.id}
              originalTranscriptSegment={quote.original_transcript_segment}
              startTime={quote.start_time}
              participantId={quote.participant_id}
              whoSaidIt={quote.who_said_it}
              content={quote.content}
            />
          ),
        })
      }
      for (const qa of qas.filter(q => q.meetingId === meet.id)) {
        highlights.push({
          note_type: 'qa',
          start_time: qa.start_time,
          end_time: qa.end_time,
          color: cnt.notesColors.qas,
          tooltip: (
            <QATooltip
              id={qa.id}
              content={qa.content}
              facilitatorQuestion={qa.facilitator_question}
              startTime={qa.start_time}
              participantId={qa.participant_id}
              whoSaidIt={qa.who_said_it}
            />
          ),
        })
      }

      highlights.sort((a, b) => a.start_time - b.start_time)
      if (highlights.length) {
        let highlightIndex = 0
        let transcriptIndex = 0
        while (highlightIndex < highlights.length && transcriptIndex < splittedTranscripts.length) {
          const transcript = splittedTranscripts[transcriptIndex]
          let sentenceIndex = 0
          while (sentenceIndex < transcript.sentences.length) {
            const sentence = transcript.sentences[sentenceIndex]
            while (highlightIndex < highlights.length && highlights[highlightIndex].end_time < sentence.start) {
              highlightIndex++
            }
            if (highlightIndex === highlights.length) break
            if (
              highlights[highlightIndex].start_time <= sentence.start &&
              highlights[highlightIndex].end_time >= sentence.start
            ) {
              transcript.sentences[sentenceIndex].highlight = highlights[highlightIndex]
            }
            sentenceIndex++
          }
          if (highlightIndex === highlights.length) break
          transcriptIndex++
        }
      }

      const newTranscripts = []
      for (const transcript of splittedTranscripts) {
        let hasHighlights = false
        const joinedSentences = []
        for (const sentence of transcript.sentences) {
          if (!joinedSentences.length) {
            joinedSentences.push(sentence)
            hasHighlights = !!sentence.highlight
            continue
          }
          if ((hasHighlights && sentence.highlight) || (!hasHighlights && !sentence.highlight)) {
            joinedSentences[joinedSentences.length - 1].text += ' ' + sentence.text
          } else {
            hasHighlights = !!sentence.highlight
            joinedSentences.push(sentence)
          }
        }
        newTranscripts.push({ ...transcript, sentences: joinedSentences })
      }

      if (!newTranscripts.length && !meet.meeting_url && !meet.processing && meet.thread_id) {
        newTranscripts.push({ id: 'error', participant_id: '', time: '', sentences: [{ text: 'Transcription failed: please verify that the file has audible content, and try again.' }] })
      }

      newMeetings.push({ id: meet.id, meeting: meet, transcripts: newTranscripts })
    }

    setTexts(newMeetings)
  }, [transcripts, meetings, notes, feedbacks, qas, quotes])

  if (meetings.length === 0 || (meetings.length > 0 && texts.every(text => text.transcripts.length === 0))) {
    return <EmptyState projectName={project.name} />
  }

  return texts.map(text => <Transcript text={text} key={text.id} />)
}
